import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-xs font-semibold ring-offset-space transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 select-none cursor-pointer',
  {
    variants: {
      variant: {
        default: 'bg-gold text-ink hover:bg-gold2 shadow-sm font-bold',
        outline: 'border border-hair bg-transparent text-txt hover:border-gold hover:text-gold2 hover:bg-glass-hover',
        secondary: 'bg-glass-3 text-txt border border-hair hover:border-hair2 hover:bg-glass-hover',
        ghost: 'text-mut hover:text-txt hover:bg-glass-hover',
        glass: 'bg-glass-2 border border-hair text-txt hover:border-gold-a22 hover:bg-glass-hover',
        destructive: 'bg-bad/20 border border-bad/30 text-bad hover:bg-bad/30',
        link: 'text-gold2 underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-7 px-3 text-[11px]',
        lg: 'h-11 px-6 text-sm',
        icon: 'h-8 w-8',
        'icon-sm': 'h-7 w-7',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
